import type { User } from '../types';

/**
 * Has this user finished the meaningful part of onboarding?
 *
 * The user record carries two related timestamps:
 *   - `onboarding.baseline_completed_at` — set when the baseline simulation
 *     finishes (the actual practice step). Persisted by both
 *     `backend/routes/baseline.js` and `frontend/src/pages/BaselineSnapshot.tsx`.
 *   - `onboarding.completed_at` — set when the user reaches the optional
 *     last step (`BaselineQuizFocus`) and picks a focus area.
 *
 * Several legitimate exit paths land users on /dashboard without ever
 * passing through `BaselineQuizFocus` (the "Exit" button on
 * `BaselineQuizSnapshot`, the brand-logo button, the "Dashboard →" button
 * on `BaselineSnapshot`). Gating the redirect on `completed_at` alone
 * meant those users got bounced through onboarding again on every login.
 *
 * The user's intuition of "I did onboarding" maps to "I finished the
 * baseline simulation", so we accept either timestamp as proof of completion.
 */
export function hasCompletedOnboarding(user: User | null | undefined): boolean {
  if (!user) return false;
  return isOnboardingComplete(user.onboarding);
}

/**
 * Variant for cases where we only have the onboarding object, not the full user.
 */
export function isOnboardingComplete(
  onboarding:
    { baseline_completed_at?: string | null; completed_at?: string | null } | null | undefined,
): boolean {
  if (!onboarding) return false;
  return !!(onboarding.baseline_completed_at || onboarding.completed_at);
}

/**
 * Which homepage dashboard view should this user see?
 *
 * VIEW SELECTION IS ACTIVITY-BASED, NOT onboarding-based. Genuine first-timers
 * (<=1 scored session) get the early-journey/setup card; anyone who has actually
 * practised gets the return-visit view, which renders their real session data and
 * adapts its diagnostic CTA on onboarding state (routing pre-baseline users to
 * /baseline-quiz). It deliberately does NOT gate on baseline completion: doing so
 * stranded active-but-pre-baseline users on the "do your first simulation" screen
 * despite a full practice history. Pre-baseline PAGE locks (/progress, /pathways)
 * are enforced separately by OnboardingRoute and are independent of this.
 *
 * `demoJourney` is a TEST-ACCOUNT-ONLY override ('new' forces early, 'full' forces
 * return-visit); it is null for every real user.
 */
export function isEarlyJourneyView(opts: {
  demoJourney?: 'new' | 'full' | null;
  totalSessions: number;
}): boolean {
  if (opts.demoJourney === 'new') return true;
  if (opts.demoJourney === 'full') return false;
  return (Number(opts.totalSessions) || 0) <= 1;
}

/**
 * Someone whose Home is the team view (MH1, MQ1). They are not sent to the
 * learner diagnostic before they can see their team. Roles rather than a
 * permission, because this runs before the permission set has loaded.
 */
export const leadsTeam = (user: any): boolean =>
  ['manager', 'admin', 'owner'].includes(String(user?.role || '').toLowerCase());
